import { useEffect, useMemo, useRef, useState } from 'react'
import { useSession, useI18n, AmountInput } from 'gunsi-auth'
import { BottomSheet } from '../components/BottomSheet.jsx'
import { Select } from '../components/Select.jsx'
import { CurrencySelect } from '../components/CurrencySelect.jsx'
import { DatePicker } from '../components/DatePicker.jsx'
import { TimePicker } from '../components/TimePicker.jsx'
import { EmojiPicker } from '../components/EmojiPicker.jsx'
import { useEven } from '../hooks/useEven.jsx'
import { useEvenI18n } from '../i18n/even.js'
import { useConfirm } from '../components/ConfirmDialog.jsx'
import { youOf, makeMemberLabel } from '../util/you.js'
import { usedCurrencies, lastUsedCurrency, expensePayers } from '../util/balances.js'
import { getRatesForDates, convert } from '../util/fx.js'
import { suggestEmoji, EXPENSE_EMOJIS } from '../util/expenseEmoji.js'
import { parseAmount, num } from '../util/amount.js'
import { fmtMoney, fmtDate } from '../format.js'

const CATEGORY_EMOJI = {
  food: '🍕', drinks: '🍻', groceries: '🛒', transport: '🚆', lodging: '🏨',
  entertainment: '🎬', utilities: '💡', shopping: '🛍️', other: '💸',
}

// Glifi delle modalità di split (stile Splitwise: =, importi, %, quote)
const SPLIT_ICONS = { equal: '=', exact: '1.23', percent: '%', shares: '⅓', adjust: '±' }
const SPLIT_MODES = ['equal', 'exact', 'percent', 'shares', 'adjust']

function todayISODate() {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function nowTime() {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ── Bozza ────────────────────────────────────────────────────────────────────
// Su mobile l'OS può sospendere o uccidere la PWA mentre il form è aperto
// (si passa a un'altra app per guardare uno scontrino e si torna): al rientro
// la pagina è ricaricata e lo state React è perso. Teniamo una bozza in LS,
// ripristinata alla riapertura dello stesso form. Slot singolo: la bozza
// interessante è sempre l'ultima.
const DRAFT_KEY = 'gunsi_even_expense_draft_v1'
const DRAFT_TTL = 24 * 60 * 60 * 1000

function readDraft(groupId, expenseId) {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null')
    if (!d || d.groupId !== groupId) return null
    if ((d.expenseId ?? null) !== (expenseId ?? null)) return null
    if (!d.at || Date.now() - d.at > DRAFT_TTL) return null
    return d.data ?? null
  } catch { return null }
}

function writeDraft(groupId, expenseId, data) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({
      groupId, expenseId: expenseId ?? null, at: Date.now(), data,
    }))
  } catch { /* quota/private mode: la bozza è un extra, non blocchiamo nulla */ }
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY) } catch { /* idem */ }
}

export function ExpenseFormSheet({ open, onClose, group, expense }) {
  const t = useEvenI18n()
  const { locale } = useI18n()
  const { session } = useSession()
  const { confirm } = useConfirm()
  const {
    addExpense, updateExpense, removeExpense,
    expenses: allExpenses, settlements: allSettlements, currency: userCurrency,
    tallySync, tallyExcluded, tallyAccess, setExpenseTallyExcluded, dateFormat,
  } = useEven()
  const you = youOf(group, session?.user?.id)
  // Checkbox opt-out Tally (PERSONALE: vale solo per l'utente loggato):
  // visibile solo se la sync è attiva per questo gruppo
  const showTallyOptOut = Boolean(tallyAccess && group && tallySync?.[group.id])

  const isEdit = Boolean(expense)
  const [description, setDescription] = useState('')
  const [amountStr, setAmountStr]     = useState('')
  const [paidBy, setPaidBy]           = useState('')
  // Multi-pagante: { memberId: importoStr } dei soli paganti attivi, più la
  // valuta con cui ciascuno ha anticipato (default = quella della spesa)
  const [multiPayer, setMultiPayer]   = useState(false)
  const [payerAmounts, setPayerAmounts] = useState({})
  const [payerCurs, setPayerCurs]     = useState({})
  const [splitMode, setSplitMode]     = useState('equal')
  const [participants, setParticipants] = useState({})
  const [date, setDate]               = useState(todayISODate())
  const [time, setTime]               = useState(nowTime())
  const [dateOpen, setDateOpen]       = useState(false)
  const [timeOpen, setTimeOpen]       = useState(false)
  const [category, setCategory]       = useState('other')
  const [notes, setNotes]             = useState('')
  const [hideTally, setHideTally]     = useState(false)
  const [cur, setCur]                 = useState('EUR')
  // Emoji: auto-suggerita dalle keyword della descrizione finché l'utente
  // non ne sceglie una a mano dal picker
  const [emoji, setEmoji]             = useState('💸')
  const [emojiManual, setEmojiManual] = useState(false)
  const [emojiOpen, setEmojiOpen]     = useState(false)

  // Il salvataggio fa un giro di rete: due tap veloci creavano due spese.
  // Il flag è una ref e non uno state perché deve valere GIÀ nel secondo
  // evento, prima che React abbia ri-renderizzato col pulsante disabilitato.
  const savingRef = useRef(false)
  const [saving, setSaving] = useState(false)

  const members = group?.members ?? []
  const memberLabel = makeMemberLabel(group)
  // Valute già usate nel gruppo (per i "recenti" del picker)
  const recentCurrencies = useMemo(() => {
    if (!group) return []
    const gExp = allExpenses.filter(e => e.groupId === group.id)
    const gSet = allSettlements.filter(s => s.groupId === group.id)
    return [...new Set([...usedCurrencies(group, gExp, gSet), userCurrency].filter(Boolean))]
  }, [group, allExpenses, allSettlements, userCurrency])

  function applyForm(f) {
    setDescription(f.description)
    setAmountStr(f.amountStr)
    setPaidBy(f.paidBy)
    setMultiPayer(f.multiPayer)
    setPayerAmounts(f.payerAmounts)
    setPayerCurs(f.payerCurs ?? {})
    setSplitMode(f.splitMode)
    setParticipants(f.participants)
    setDate(f.date)
    setTime(f.time)
    setCategory(f.category)
    setNotes(f.notes)
    setHideTally(f.hideTally)
    setCur(f.cur)
    setEmoji(f.emoji)
    setEmojiManual(f.emojiManual)
  }

  // Init all'apertura. Le dipendenze sono gli ID, non gli oggetti: `refresh()`
  // (focus/visibilitychange) rimpiazza groups/expenses con istanze nuove dal
  // server e con `[open, expense, group]` questo effect ripartiva azzerando
  // quello che l'utente aveva già scritto.
  useEffect(() => {
    if (!open) return
    let base
    if (expense) {
      const pl = expensePayers(expense)
      const multi = pl.length > 1
      const map = {}
      for (const p of expense.participants ?? []) map[p.memberId] = p.value
      base = {
        description: expense.description ?? '',
        amountStr: String(expense.amount ?? ''),
        paidBy: expense.paidBy,
        multiPayer: multi,
        // In edit si rimette davanti quello che l'utente aveva digitato:
        // l'importo originale se aveva anticipato in un'altra valuta
        payerAmounts: multi
          ? Object.fromEntries(pl.map(p => [p.memberId, String(p.origAmount ?? p.amount)]))
          : {},
        payerCurs: multi
          ? Object.fromEntries(pl.filter(p => p.origCurrency).map(p => [p.memberId, p.origCurrency]))
          : {},
        splitMode: expense.splitMode || 'equal',
        participants: map,
        date: expense.date?.slice(0, 10) || todayISODate(),
        time: expense.date && expense.date.length > 10 ? expense.date.slice(11, 16) : '12:00',
        category: expense.category || 'other',
        notes: expense.notes ?? '',
        hideTally: Boolean(tallyExcluded?.[expense.id]),
        cur: expense.currency || group?.currency || 'EUR',
        emoji: expense.emoji || CATEGORY_EMOJI[expense.category] || '💸',
        emojiManual: false,
      }
    } else {
      // All members participate by default
      const map = {}
      for (const m of members) map[m.id] = 1
      base = {
        description: '',
        amountStr: '',
        paidBy: you?.id || members[0]?.id || '',
        multiPayer: false,
        payerAmounts: {},
        payerCurs: {},
        splitMode: 'equal',
        participants: map,
        date: todayISODate(),
        time: nowTime(),
        category: 'other',
        notes: '',
        hideTally: false,
        cur: lastUsedCurrency(group, allExpenses, userCurrency),
        emoji: '💸',
        emojiManual: false,
      }
    }
    const draft = readDraft(group?.id, expense?.id)
    applyForm(draft ? { ...base, ...draft } : base)
    // Il blocco anti-doppio-invio va sciolto a ogni apertura: questo
    // componente NON si smonta alla chiusura dello sheet (il BottomSheet
    // smonta solo il proprio contenuto), quindi il flag sopravviveva al
    // salvataggio e il form si riapriva col pulsante già disabilitato.
    savingRef.current = false
    setSaving(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expense?.id, group?.id])

  // Salvataggio bozza (debounced): copre il caso in cui la pagina viene
  // ricaricata da zero perché l'OS ha scaricato la PWA dalla memoria.
  const draftSnapshot = JSON.stringify({
    description, amountStr, paidBy, multiPayer, payerAmounts, splitMode,
    participants, date, time, category, notes, hideTally, cur, emoji, emojiManual,
  })
  useEffect(() => {
    if (!open || !group) return
    // Form nuovo e ancora vuoto: niente da conservare
    if (!isEdit && !description.trim() && !amountStr.trim()) { clearDraft(); return }
    const id = setTimeout(() => writeDraft(group.id, expense?.id, JSON.parse(draftSnapshot)), 300)
    return () => clearTimeout(id)
  }, [open, group?.id, expense?.id, isEdit, description, amountStr, draftSnapshot])

  // Chiusura volontaria (salva, annulla, backdrop, elimina) = la bozza non
  // serve più. Se invece la pagina muore, nessuno la cancella e al rientro
  // viene ripristinata.
  function closeAndDiscard() {
    clearDraft()
    onClose()
  }

  const parsedAmount = parseAmount(amountStr)
  const amount = Number.isFinite(parsedAmount) ? parsedAmount : 0
  // Importo digitato ma non interpretabile: lo diciamo, invece di lasciare
  // il pulsante muto
  const amountInvalid = amountStr.trim() !== '' && !(parsedAmount > 0)
  const activeMembers = members.filter(m => participants[m.id] !== undefined)

  const shareInfo = useMemo(() => {
    if (splitMode === 'equal') {
      return { per: activeMembers.length ? amount / activeMembers.length : 0, remaining: 0 }
    }
    if (splitMode === 'exact') {
      const sum = activeMembers.reduce((a, m) => a + num(participants[m.id]), 0)
      return { per: null, remaining: Math.round((amount - sum) * 100) / 100 }
    }
    if (splitMode === 'percent') {
      const sum = activeMembers.reduce((a, m) => a + num(participants[m.id]), 0)
      return { per: null, remaining: Math.round((100 - sum) * 100) / 100, isPercent: true }
    }
    if (splitMode === 'shares') {
      const sum = activeMembers.reduce((a, m) => a + num(participants[m.id]), 0)
      return { per: null, totalShares: sum, isShares: true }
    }
    if (splitMode === 'adjust') {
      // Extra personali scalati dal totale; il resto va in parti uguali
      const sumAdj = activeMembers.reduce((a, m) => a + num(participants[m.id]), 0)
      const rest = amount - sumAdj
      return {
        per: activeMembers.length ? rest / activeMembers.length : 0,
        remaining: Math.round(rest * 100) / 100,
        isAdjust: true,
      }
    }
    return { per: 0, remaining: 0 }
  }, [splitMode, amount, participants, activeMembers])

  // Tassi storici della data della spesa: servono solo se qualcuno ha
  // anticipato in una valuta diversa da quella della spesa
  const needsFx = useMemo(
    () => Object.keys(payerAmounts).some(mid => (payerCurs[mid] || cur) !== cur),
    [payerAmounts, payerCurs, cur],
  )
  const [fxRates, setFxRates] = useState(null)
  const [fxLoading, setFxLoading] = useState(false)
  useEffect(() => {
    if (!needsFx || !date) return
    let cancelled = false
    setFxLoading(true)
    getRatesForDates([date]).then(map => {
      if (cancelled) return
      setFxRates(map?.[date] ?? null)
      setFxLoading(false)
    })
    return () => { cancelled = true }
  }, [needsFx, date])

  // Contributo di un pagante NELLA VALUTA DELLA SPESA. null = servirebbe un
  // tasso che non abbiamo (offline): meglio bloccare che salvare un bilancio
  // sbagliato.
  function payerContribution(mid) {
    const raw = num(payerAmounts[mid])
    const pc = payerCurs[mid] || cur
    if (pc === cur) return raw
    if (!fxRates) return null
    const c = convert(raw, pc, cur, fxRates)
    return c == null ? null : Math.round(c * 100) / 100
  }

  // Multi-pagante: la somma degli anticipi deve fare il totale
  const payerContribs = useMemo(
    () => Object.keys(payerAmounts).map(mid => payerContribution(mid)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payerAmounts, payerCurs, cur, fxRates],
  )
  const payersMissingRate = payerContribs.some(c => c == null)
  const payersSum = payerContribs.reduce((a, c) => a + (c ?? 0), 0)
  const payersRemaining = Math.round((amount - payersSum) * 100) / 100
  const payersInvalid = multiPayer &&
    (payersMissingRate || Math.abs(payersRemaining) > 0.005 || Object.keys(payerAmounts).length === 0)

  // Split non quadrato = non salvabile: importi esatti ≠ totale, percentuali
  // ≠ 100, o nessuna quota assegnata
  const splitInvalid =
    (splitMode === 'exact'   && Math.abs(shareInfo.remaining) > 0.005) ||
    (splitMode === 'percent' && Math.abs(shareInfo.remaining) > 0.005) ||
    (splitMode === 'shares'  && !(shareInfo.totalShares > 0)) ||
    // adjust: gli extra non possono superare il totale (resto negativo)
    (splitMode === 'adjust'  && shareInfo.remaining < -0.005) ||
    payersInvalid

  // Passaggio singolo ⇄ multi pagante: al primo giro precompilo il pagante
  // corrente con l'intero importo, così basta correggere le cifre
  function toggleMultiPayer() {
    setMultiPayer(v => {
      const next = !v
      if (next) {
        setPayerAmounts(prev => (Object.keys(prev).length ? prev : { [paidBy]: amountStr || '' }))
      }
      return next
    })
  }
  function togglePayer(mid) {
    setPayerAmounts(p => {
      const next = { ...p }
      if (next[mid] !== undefined) delete next[mid]
      else next[mid] = ''
      return next
    })
  }
  function setPayerAmount(mid, value) {
    setPayerAmounts(p => ({ ...p, [mid]: value }))
  }

  // Cambio modalità split: i valori della modalità precedente non hanno
  // senso nella nuova (es. gli "1" di equal letti come 1€ esatto) → reset.
  // equal/shares ripartono da 1, exact/percent da vuoto.
  function changeSplitMode(m) {
    setSplitMode(m)
    setParticipants(p => {
      const next = {}
      // adjust: extra a 0 di default (nessuno ha ancora consumato in più)
      for (const id of Object.keys(p)) next[id] = (m === 'equal' || m === 'shares') ? 1 : ''
      return next
    })
  }

  function toggleMember(mid) {
    setParticipants(p => {
      const next = { ...p }
      if (next[mid] !== undefined) delete next[mid]
      else next[mid] = splitMode === 'equal' ? 1 : 0
      return next
    })
  }

  function setMemberValue(mid, value) {
    setParticipants(p => ({ ...p, [mid]: value }))
  }

  function onDescriptionChange(value) {
    setDescription(value)
    if (!emojiManual) {
      const suggested = suggestEmoji(value)
      // In edit niente fallback: senza match si tiene l'emoji esistente
      // (potrebbe essere stata scelta a mano in passato)
      if (suggested) setEmoji(suggested)
      else if (!isEdit) setEmoji('💸')
    }
  }

  // Controvalore in denaro della quota di un member (percent/shares)
  function memberComputed(mid) {
    const v = num(participants[mid])
    if (splitMode === 'percent') return amount * v / 100
    if (splitMode === 'shares') {
      const sum = shareInfo.totalShares || 0
      return sum > 0 ? amount * v / sum : 0
    }
    if (splitMode === 'adjust') return v + (shareInfo.per ?? 0)
    return 0
  }

  async function onSubmit(e) {
    e.preventDefault()
    if (savingRef.current) return
    if (!amount || splitInvalid) return
    const partList = activeMembers.map(m => ({
      memberId: m.id,
      value: splitMode === 'equal' ? 1 : num(participants[m.id]),
    }))
    if (partList.length === 0) return
    // payers[] solo in multi-pagante; `paidBy` resta valorizzato (il pagante
    // maggiore) per compatibilità con le viste che leggono il singolo
    // `amount` va salvato nella valuta della spesa (conversione congelata al
    // tasso della data); l'importo digitato resta come origAmount
    const payerList = multiPayer
      ? Object.keys(payerAmounts)
          .map(memberId => {
            const raw = num(payerAmounts[memberId])
            const pc = payerCurs[memberId] || cur
            const contrib = payerContribution(memberId)
            return pc === cur
              ? { memberId, amount: raw }
              : { memberId, amount: contrib ?? 0, origAmount: raw, origCurrency: pc }
          })
          .filter(p => p.amount > 0.004)
          .sort((a, b) => b.amount - a.amount)
      : []
    const mainPayer = payerList[0]?.memberId || paidBy
    if (!mainPayer) return
    const payload = {
      groupId: group.id,
      amount,
      currency: cur,
      paidBy: mainPayer,
      payers: payerList,
      splitMode,
      participants: partList,
      date: `${date}T${time}`,
      category,
      emoji,
      description: description.trim(),
      notes: notes.trim(),
    }
    savingRef.current = true
    setSaving(true)
    try {
      if (isEdit) {
        await updateExpense(expense.id, payload)
        // Flag personale: solo se cambiato rispetto allo stato salvato
        if (hideTally !== Boolean(tallyExcluded?.[expense.id])) {
          await setExpenseTallyExcluded(expense.id, hideTally)
        }
      } else {
        const created = await addExpense(payload)
        if (hideTally && created?.id) {
          await setExpenseTallyExcluded(created.id, true)
        }
      }
    } catch (err) {
      // Salvataggio fallito: si deve poter riprovare
      savingRef.current = false
      setSaving(false)
      throw err
    }
    closeAndDiscard()
  }

  async function onDelete() {
    if (!expense) return
    const ok = await confirm({
      message: t.expense.deleteConfirm,
      okLabel: t.common.delete,
      danger: true,
    })
    if (!ok) return
    await removeExpense(expense.id)
    closeAndDiscard()
  }

  if (!group) return null

  return (
    <BottomSheet open={open} onClose={closeAndDiscard} className="bsheet-form">
      <form onSubmit={onSubmit} className="form-sheet">
        <header className="form-sheet-head">
          <button type="button" className="btn btn-ghost" onClick={closeAndDiscard}>{t.expense.cancel}</button>
          <h2>{isEdit ? t.expense.editTitle : t.expense.title}</h2>
          <button type="submit" className="btn btn-primary" disabled={saving || splitInvalid || amountInvalid || !amount}>{t.expense.save}</button>
        </header>

        <div className="form-sheet-body">
        <div className="form-row">
          <label>{t.expense.description}</label>
          <div className="desc-row">
            <button
              type="button"
              className="emoji-btn"
              onClick={() => setEmojiOpen(true)}
              aria-label={t.newGroup.emoji}
            >{emoji}</button>
            <input
              type="text"
              value={description}
              onChange={e => onDescriptionChange(e.target.value)}
              placeholder={t.expense.descPh}
              autoFocus
            />
          </div>
        </div>

        <div className="form-row">
          <label>{t.expense.amount}</label>
          <div className="amount-input-row">
            <AmountInput
              value={amountStr}
              onChange={setAmountStr}
              placeholder="0.00"
            />
            <CurrencySelect
              value={cur}
              onChange={setCur}
              recentCodes={recentCurrencies}
              title={t.common.currency}
              searchPlaceholder={t.common.searchCurrencyPh}
            />
          </div>
          {amountInvalid && (
            <div className="split-feedback warn">{t.expense.amountInvalid}</div>
          )}
        </div>

        <div className="form-row">
          <div className="row-head">
            <label>{t.expense.paidBy}</label>
            <button type="button" className="btn btn-ghost btn-small" onClick={toggleMultiPayer}>
              {multiPayer ? t.expense.onePayer : t.expense.multiPayer}
            </button>
          </div>
          {!multiPayer ? (
            <Select
              value={paidBy}
              onChange={setPaidBy}
              options={members.map(m => ({ value: m.id, label: memberLabel(m.id) }))}
              label={t.expense.paidBy}
            />
          ) : (
            <>
              <div className="participant-list">
                {members.map(m => {
                  const checked = payerAmounts[m.id] !== undefined
                  return (
                    <div key={m.id} className={`participant-row${checked ? ' active' : ''}`}>
                      <label className="participant-name">
                        <input type="checkbox" checked={checked} onChange={() => togglePayer(m.id)} />
                        <span>{m.name}</span>
                      </label>
                      {checked && (
                        <div className="participant-val-wrap">
                          <AmountInput
                            value={payerAmounts[m.id] ?? ''}
                            onChange={v => setPayerAmount(m.id, v)}
                            placeholder="0.00"
                            className="participant-value"
                          />
                          {/* Chi paga può aver anticipato in un'altra valuta
                              (la carta addebita in euro un conto in lek) */}
                          <CurrencySelect
                            value={payerCurs[m.id] || cur}
                            onChange={v => setPayerCurs(p => ({ ...p, [m.id]: v }))}
                            recentCodes={recentCurrencies}
                            title={t.common.currency}
                            searchPlaceholder={t.common.searchCurrencyPh}
                          />
                        </div>
                      )}
                      {/* Equivalente nella valuta della spesa: è quello che
                          conta nel bilancio, e va visto mentre si digita */}
                      {checked && (payerCurs[m.id] || cur) !== cur && (
                        <div className="payer-conv">
                          {fxLoading
                            ? '…'
                            : payerContribution(m.id) == null
                              ? t.expense.rateMissing
                              : `≈ ${fmtMoney(payerContribution(m.id), cur)}`}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              <div className={`split-feedback${(payersMissingRate || Math.abs(payersRemaining) > 0.005) ? ' warn' : ''}`}>
                {payersMissingRate
                  ? t.expense.rateMissing
                  : `${t.expense.remaining}: ${fmtMoney(payersRemaining, cur)}`}
              </div>
            </>
          )}
        </div>

        <div className="form-row">
          <label>{t.expense.date}</label>
          <div className="datetime-row">
            <button type="button" className="date-btn" onClick={() => setDateOpen(true)}>
              {fmtDate(date, undefined, dateFormat)}
            </button>
            <button type="button" className="time-btn" onClick={() => setTimeOpen(true)}>
              {time}
            </button>
          </div>
        </div>

        <div className="form-row">
          <label>{t.expense.splitMode}</label>
          <div className="split-tabs">
            {SPLIT_MODES.map(m => (
              <button
                key={m}
                type="button"
                className={splitMode === m ? 'active' : ''}
                onClick={() => changeSplitMode(m)}
                title={t.expense.split[m]}
              >
                <span className="split-icon">{SPLIT_ICONS[m]}</span>
                <span className="split-lbl">{t.expense.splitShort[m]}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="form-row">
          <label>{t.expense.participants}</label>
          <div className="participant-list">
            {members.map(m => {
              const checked = participants[m.id] !== undefined
              return (
                <div key={m.id} className={`participant-row${checked ? ' active' : ''}`}>
                  <label className="participant-name">
                    <input type="checkbox" checked={checked} onChange={() => toggleMember(m.id)} />
                    <span>{m.name}</span>
                  </label>
                  {checked && (splitMode === 'percent' || splitMode === 'shares' || splitMode === 'adjust') && (
                    <span className="participant-share">
                      {fmtMoney(memberComputed(m.id), cur)}
                    </span>
                  )}
                  {checked && splitMode !== 'equal' && (
                    <div className="participant-val-wrap">
                      <AmountInput
                        value={participants[m.id] ?? ''}
                        onChange={v => setMemberValue(m.id, v)}
                        placeholder={splitMode === 'shares' ? '1' : splitMode === 'percent' ? '0' : '0.00'}
                        className="participant-value"
                      />
                      <span className="participant-unit">
                        {splitMode === 'percent' ? '%' : splitMode === 'shares' ? '×' : splitMode === 'adjust' ? `+${cur}` : cur}
                      </span>
                    </div>
                  )}
                  {checked && splitMode === 'equal' && (
                    <span className="participant-share">
                      {fmtMoney(shareInfo.per ?? 0, cur)}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
          {splitMode === 'exact' && (
            <div className={`split-feedback${Math.abs(shareInfo.remaining) > 0.005 ? ' warn' : ''}`}>
              {t.expense.remaining}: {fmtMoney(shareInfo.remaining, cur)}
            </div>
          )}
          {splitMode === 'percent' && (
            <div className={`split-feedback${Math.abs(shareInfo.remaining) > 0.005 ? ' warn' : ''}`}>
              {t.expense.remaining}: {shareInfo.remaining}%
            </div>
          )}
          {splitMode === 'adjust' && (
            <div className={`split-feedback${shareInfo.remaining < -0.005 ? ' warn' : ''}`}>
              {t.expense.adjustHint
                .replace('{rest}', fmtMoney(shareInfo.remaining, cur))
                .replace('{each}', fmtMoney(shareInfo.per ?? 0, cur))}
            </div>
          )}
        </div>

        <div className="form-row">
          <label>{t.expense.notes}</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={t.expense.notesPh}
            rows={2}
          />
        </div>

        {showTallyOptOut && (
          <div className="form-row">
            <label className="toggle-row">
              <input type="checkbox" checked={hideTally} onChange={e => setHideTally(e.target.checked)} />
              <span>{t.expense.hideTally}</span>
            </label>
          </div>
        )}

        {isEdit && (
          <div className="form-row">
            <button type="button" className="btn btn-danger" onClick={onDelete}>{t.expense.delete}</button>
          </div>
        )}

        <DatePicker
          open={dateOpen}
          value={date}
          onChange={setDate}
          onClose={() => setDateOpen(false)}
          locale={locale}
          title={t.expense.date}
          todayLabel={t.common.today}
        />
        <TimePicker
          open={timeOpen}
          value={time}
          onChange={setTime}
          onClose={() => setTimeOpen(false)}
          title={t.expense.time}
          nowLabel={t.common.now}
        />
        <BottomSheet open={emojiOpen} onClose={() => setEmojiOpen(false)} className="bsheet-select">
          <div className="select-sheet">
            <header className="select-sheet-head">{t.newGroup.emoji}</header>
            <EmojiPicker
              value={emoji}
              onChange={(em) => { setEmoji(em); setEmojiManual(true); setEmojiOpen(false) }}
              searchPlaceholder={t.common.searchEmojiPh}
              quick={EXPENSE_EMOJIS}
            />
          </div>
        </BottomSheet>
        </div>
      </form>
    </BottomSheet>
  )
}
